// Смена статуса занятости. Панель, а не модалка: статус меняется туда и обратно, необратимого
// в нём нет — необратимо только увольнение, и оно живёт отдельной модалкой.
//
// Основание необязательно на сервере, но подписано так, чтобы его хотелось заполнить: без него
// в истории останется «резерв → отпуск» без единого слова о том, почему, и через полгода это
// спрашивают в чате у того, кто уже не помнит.
//
// Статус «уволен» из списка убран намеренно. Увольнение — это не смена статуса, а отдельная
// операция с обязательной причиной, датой и закрытием назначений (эндпоинт /fire). Оставить
// его здесь значило бы дать тихий обход всей этой процедуры.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';
import { useLocation } from 'react-router';

import { EMPLOYEE_STATUSES, isEmployeeStatus } from '@/shared/api/endpoints/people';
import type { EmployeeStatus } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import { Skeleton } from '@/shared/ui/screen-state';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { STATUS_TITLES } from '../dictionaries';
import { useEmployeeQuery, useStatusMutation } from '../queries';
import { registryTabAtLocation } from '../route';

const EDIT_PERMISSION = 'core.employee.edit';

export function StatusPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const employee = useEmployeeQuery(id);
  // Панель не получает маршрут раздела, а подтверждению надо знать, на какой вкладке реестра
  // человек нажал «Сменить статус»: строка уйдёт из неё, и подтверждение назовёт, куда.
  const location = useLocation();
  const mutation = useStatusMutation(
    id,
    registryTabAtLocation(location.pathname, location.search),
  );

  const [status, setStatus] = useState(NO_VALUE);
  const [basis, setBasis] = useState('');
  const [statusError, setStatusError] = useState<string | undefined>(undefined);
  const [gateNote, setGateNote] = useState<string | null>(null);

  const current = employee.data?.status;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Менять статус может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Статус уже меняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (!isEmployeeStatus(status)) {
      setStatusError('Выберите новый статус — иначе непонятно, на какой менять.');
      return;
    }
    if (status === current) {
      setStatusError(
        `Человек уже в состоянии «${STATUS_TITLES[status]}». Выберите другой статус — запись «из А в А» истории ничего не добавит.`,
      );
      return;
    }
    setStatusError(undefined);
    setGateNote(null);
    mutation.mutate(
      { status: status as EmployeeStatus, basis: basis.trim() === '' ? null : basis.trim() },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {/* Скелет вместо слова «загружаем…»: строка несёт и имя, и нынешний статус, то есть
          ровно то, на что человек смотрит перед сменой статуса. Текст на её месте читается
          как ответ. */}
      {/* Скелет показывается ТОЛЬКО пока запрос идёт. Условие «данных нет» держало бы его
          вечно: карточка может не приехать вовсе — например, панель открыли по ссылке на
          человека, которого больше не видно по правам. Вечный скелет читается как «сейчас
          догрузится» и не догружается никогда. */}
      {employee.data === undefined && employee.isPending ? (
        <Skeleton className='h-4 w-56' />
      ) : employee.data === undefined ? null : (
        <p className='text-paragraph-xs text-text-sub-600'>
          {`${employee.data.display_name} · сейчас «${STATUS_TITLES[employee.data.status]}»`}
        </p>
      )}

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но статус не сменится. Право
            выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <SelectField
        id='status-panel-value'
        label='Новый статус'
        required
        value={status}
        onChange={setStatus}
        options={EMPLOYEE_STATUSES.filter((code) => code !== 'fired').map((code) => ({
          value: code,
          label: STATUS_TITLES[code],
        }))}
        placeholder='Выберите статус'
        error={statusError}
        hint='Увольнение делается отдельно, кнопкой «Уволить»: там обязательны дата и причина.'
      />

      <TextField
        id='status-panel-basis'
        label='Основание'
        value={basis}
        onChange={setBasis}
        placeholder='Приказ № 12 от 01.08.2026'
        hint='Останется в истории статусов. Без него через полгода никто не вспомнит, почему статус сменился.'
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Меняем статус…' : 'Сменить статус'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
